import React, { useContext, useEffect, useState } from 'react'
import { Box, Divider, Grid, Typography } from '@mui/material'
import { GetStaticProps } from 'next'
import { NextRouter, useRouter } from 'next/router'
import { sortBy } from 'ramda'

import ManageStorage from '@ecm/capability/ManageStorage'
import { Address } from '@ecm/data/Address'
import { Cart } from '@ecm/data/Cart'
import { CartInteraction } from '@ecm/data/Ecommerce'
import { getOrElse } from '@ecm/data/Maybe'
import { Plan } from '@ecm/data/Plan'
import { runClientEff, runEff } from '@ecm/effect'
import { trackPricingPlans } from '@ecm/effect/client/Ecommerce/TrackEcommerce'
import { ClientStorageKey } from '@ecm/effect/client/ManageStorage'
import { UserAuthInfoContext } from '@ecm/pages/_app'
import IconInfoBlock from '@ecm/ui/component/block/IconInfoBlock'
import PlansBlock from '@ecm/ui/component/block/PlansBlock'
import PlanTabsBlock from '@ecm/ui/component/block/PlanTabsBlock'
import Color from '@ecm/ui/element/Color'
import DefaultLayout from '@ecm/ui/layout/Default'
import { filterPlans } from '@ecm/utils/filterPlans'

export const selectPlan = (router: NextRouter, p: Plan, a?: Address) => {
  // Track addToCart only for the restricted plans
  // or when the address is pre-selected from addresses page
  if (p.restrictedAddress || (a && a?.slug !== 'Beaverton_OR_STR_Gemini')) {
    trackPricingPlans([p], a, undefined, CartInteraction.AddToCart)
  }

  runClientEff((eff: ManageStorage<ClientStorageKey>) => {
    const cart: Partial<Cart> = {
      addons: [],
      addresses: a ? [a] : [],
      plan: p,
    }
    eff.updateStorageItem<Cart>('cart', cart)
  })

  router.push('/checkout/addresses').then(() => window.scrollTo(0, 0))
}

export type PricingTabKind = 'mailbox' | 'mailroom'

type PricingProps = {
  plans: Plan[]
}

const PricingPage = ({ plans }: PricingProps) => {
  const router = useRouter()
  const { handleCurrentAuthenticatedUser, user } = useContext(UserAuthInfoContext)
  const [selectedTab, setSelectedTab] = useState<PricingTabKind>('mailbox')
  const [preSelectedAddress, setPreSelectedAddress] = useState<Address | undefined>(undefined)
  const [savedCart, setSavedCart] = useState<Cart>()
  const { mailBoxPlans, mailRoomPlans } = filterPlans(plans)

  useEffect(() => {
    runClientEff((eff: ManageStorage<ClientStorageKey>) => {
      const notValidCart = getOrElse(eff.getStorageItem<boolean>('notValidCart'), undefined)
      if (!user) {
        handleCurrentAuthenticatedUser()
        return
      }
      if (!notValidCart) {
        return
      }
      if (!router.isReady || !savedCart) {
        setSavedCart(getOrElse(eff.getStorageItem<Cart>('cart'), undefined))
        return
      }
      if (user && notValidCart && savedCart) {
        eff.removeStorageItem('notValidCart')
        router.push('/checkout/billing')
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, user, savedCart])

  useEffect(() => {
    if (!router.isReady) {
      return
    }

    const { address } = router.query
    if (address && !Array.isArray(address)) {
      runClientEff(async eff => {
        const preSelectedAddress = await eff.getAddress(address)
        setPreSelectedAddress(preSelectedAddress)
        trackPricingPlans(plans, preSelectedAddress)
      })
    } else {
      trackPricingPlans(plans)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.address])

  return (
    <DefaultLayout hideFinalCta seoPage="pricing">
      <Box
        textAlign="center"
        bgcolor={Color.smoke.normal}
        padding={{ md: '4em 6em', xs: '2em 1em' }}
      >
        <Typography variant="h2" component="h1" color={Color.saint.darker}>
          Choose the right virtual mail plan for you
        </Typography>
        <Typography variant="p2" color={Color.saint.lighter}>
          Select a flexible plan based on your postal mail needs.
        </Typography>
      </Box>
      <Grid
        container
        direction="column"
        margin="0 !important"
        padding="0 !important"
        minHeight="732px"
      >
        <div id="plans-table">
          <PlanTabsBlock selectedTab={selectedTab} setSelectedTab={setSelectedTab} />

          {selectedTab === 'mailbox' && (
            <>
              <PlansBlock
                router={router}
                preSelectedAddress={preSelectedAddress}
                plans={sortBy(p => p.price, mailBoxPlans)}
              />

              <IconInfoBlock header="All MailBox plans include" />
            </>
          )}

          {selectedTab === 'mailroom' && (
            <>
              <PlansBlock
                router={router}
                preSelectedAddress={preSelectedAddress}
                plans={sortBy(p => p.price, mailRoomPlans)}
              />

              <IconInfoBlock header="All MailRoom plans include" />
            </>
          )}
        </div>
      </Grid>

      <Divider sx={{ borderColor: Color.smoke.darker }} />
    </DefaultLayout>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const isPreview = false // Static content mode - no preview
  const plans: Plan[] = await runEff(async eff => {
    return eff.getPlans(isPreview)
  })

  return {
    props: {
      plans: plans,
    },
  }
}

export default PricingPage
